import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useForm from '@sm/hooks/useForm';

import Button from '@components/custom/Button';
import Input from '@components/custom/Input';
import ModuleHeader from '@components/ModuleHeader';
import Spinner from '@components/custom/Spinner';

import '@assets/css/myAccount.css';

import { crudActions } from "@sm/services/crudActions";
import { showAlert } from '@sm/store/alertSlice';

export default function MyAccount() {
  const fields = {
    name: "",
    phone: "",
    skype: "",
    username: "",
    currentPassword: null,
    newPassword: null,
    role: "",
    whiteLabels: ""
  };

  const fieldRules = [{
    name: "name",
    type: "isString",
    rules: {
      required: true
    }
  }, {
    name: "phone",
    type: "isString",
    rules: {
      required: false
    }
  }, {
    name: "skype",
    type: "isString",
    rules: {
      required: false
    }
  }, {
    name: "currentPassword",
    type: "isString",
    rules: {
      required: false
    }
  }, {
    name: "newPassword",
    type: "isPattern",
    rules: {
      required: false,
      pattern: "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})",
      customErrorMessage: "Password must contain at least 1 lowercase, 1 uppercase, 1 numeric, 1 special character and must be 8 characters or longer"
    }
  }];

  const { form, setField, validateForm } = useForm(fields, fieldRules);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    crudActions.get(`v1/myaccount`)
      .then((accountData) => {
        setIsLoading(false);
        if (accountData) {
          setField('name', accountData.name);
          setField('phone', accountData.phone);
          setField('skype', accountData.skype);
          setField('username', accountData.username);
          setField('role', accountData.role);
          setField('whiteLabels', accountData.whiteLabels);
        }
      });
  }, [setField]);

  function onValueChange(event, fieldName) {
    const { value } = event.target;
    setField(fieldName, value);
    
    if (isSubmitClicked) {
      validateForm();
    }
  };

  function onConfirmPasswordChange(event) {
    setConfirmPassword(event.target.value);
    setConfirmPasswordTouched(true);
  };

  function arePasswordsIdentical() {
    if (!form.currentPassword && !form.newPassword) {
      return true;
    }

    return form.newPassword === confirmPassword;
  };

  function submitAccountSettings() {
    const isFormValid = validateForm();
    setIsSubmitClicked(true);

    if (isFormValid && arePasswordsIdentical()) {
      setIsSubmitting(true);
  
      const data = Object.assign(form.data(), {
        fullName: form.name,
        ...(!form.newPassword && { newPassword: null })
      });
      crudActions.put(`v1/myaccount`, data)
        .then(() => {
          setIsSubmitting(false);
          setConfirmPasswordTouched(false);
          dispatch(showAlert('success', 'Your data was successfully updated.'));
        })
        .catch(err => {
          dispatch(showAlert('danger', err?.message));
          setIsSubmitting(false);
          setConfirmPasswordTouched(false);
        });
    }
  };

  return (
    <div className="col-12 module my-account">
      <div className='component shadow mb-4 position-relative'>
        <div className='row'>
          <ModuleHeader
            id="account_circle"
            title="Account"
          />
        </div>
      
        {isLoading && <Spinner /> }
      
        <form autoComplete="off">
          <input id="new-password" type="password" autoComplete="new-password" className="d-none"/>
          <div className='row'>
            <div className='col-12 row-title'>
              <h5>User information</h5>
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='name'
                label='Full Name'
                error={ form.errors.has('name') }
                errorMessage={ form.errors.get('name') }
                value={ form.name || '' }
                onChange={ (e) => onValueChange(e, "name") }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3  mb-4 mb-lg-0'>
              <Input
                id='phone'
                label='Phone'
                error={ form.errors.has('phone') }
                errorMessage={ form.errors.get('phone') }
                value={ form.phone || '' }
                onChange={ (e) => onValueChange(e, "phone") }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='skype'
                label='Skype'
                error={ form.errors.has('skype') }
                errorMessage={ form.errors.get('skype') }
                value={ form.skype || '' }
                onChange={ (e) => onValueChange(e, "skype") }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='username'
                label='Email/username'
                disabled
                value={ form.username || '' }
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-12 row-title'>
              <h5>Change Password</h5>
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='currentPassword'
                label='Current Password'
                type='password'
                error={ form.errors.has('currentPassword') }
                errorMessage={ form.errors.get('currentPassword') }
                value={ form.currentPassword || '' }
                onChange={ (e) => onValueChange(e, "currentPassword") }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='newPassword'
                label='New Password'
                type='password'
                error={ form.errors.has('newPassword') }
                errorMessage={ form.errors.get('newPassword') }
                value={ form.newPassword || '' }
                onChange={ (e) => onValueChange(e, "newPassword") }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='confirmPassword'
                label='Repeat New Password'
                type='password'
                error={ !arePasswordsIdentical() && confirmPasswordTouched }
                errorMessage={ !confirmPassword ? 'Please, confirm the password.'  : 'Passwords are not identical.' }
                value={ confirmPassword || '' }
                onChange={ (e) => onConfirmPasswordChange(e) }
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-12 row-title'>
              <h5>Access</h5>
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='userLevel'
                label='User Level'
                disabled
                value={ form.role }
              />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-3 mb-4 mb-lg-0'>
              <Input
                id='whiteLabels'
                label='White Label'
                disabled
                value={ form.whiteLabels }
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-12 d-flex justify-content-end mt-4'>
              <Button
                label='Save'
                disabled={ isSubmitting }
                onClick={ () => submitAccountSettings() }
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
